import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "row gap-02" }

import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import { useAppUserStore } from "@/stores/appUser";
import EsParkingForm from "@/components/form/esParkingForm.vue";
import EsConfirmDialog from '@/components/modals/esConfirmDialog.vue';
import {
  ApiError, ParkingAvailable, PersonApi, 
} from "@/apiGenerate/medserver";
import { medServerApiConfig } from "@/config/medserver-api.config";
import { Column, DataTable, useToast } from "primevue";
import { toastError, toastSuccess } from "@/utils/toast-utils";


export default /*@__PURE__*/_defineComponent({
  __name: 'esParkingSettings',
  setup(__props) {

const { t } = useI18n();
const appUserStore = useAppUserStore();
const toast = useToast();

const dropd = ref("");
const isConfirmModalOpened = ref(false);
const isFormModalOpened = ref(false);
const parkings = ref<ParkingAvailable[]>([]);
const currentParking = ref<ParkingAvailable>({
  doctorUuid : '',
  uuid: '',
  parking: ''
})

const personApi = new PersonApi(medServerApiConfig);
const pwr = appUserStore.currentHealthGroupMembership?.pwr;

/**
 * Opens the add parking form modal with an empty parking object.
 */
const openAddForm = () => {
  currentParking.value = {
    doctorUuid : pwr.personWithRoleId,
    uuid: '',
    parking: ''
  };
  isFormModalOpened.value = true;
};

/**
 * Opens the update parking form modal with the given parking data.
 * @param parking - The parking data to update.
 */
const openUpdateForm = (parking: ParkingAvailable) => {
  dropd.value = "";
  currentParking.value = { ...parking };
  isFormModalOpened.value = true;
};

const onParkingModalClose = () => {
  isFormModalOpened.value = false;
}

/**
 * Closes the parking form modal and either updates or creates parking data
 * depending on the form's edit state.
 * @param formData - The submitted form data, including confirmation and editing flags.
 */
const onParkingModalConfirm = async (parking: ParkingAvailable) => {
  if (!pwr) return;

  let request: () => Promise<ParkingAvailable>;
  if (parking.uuid) {
    request = () => personApi.updateParking({ 
      doctorId: pwr.personWithRoleId,
      parkingId: parking.uuid,
      createAvailableParking: { parking: parking.parking }
    });
  } else {
    request = () => personApi.createParking({
      doctorId: pwr.personWithRoleId,
      createAvailableParking: { parking: parking.parking }
    });
  }

  await request()
    .then(() => {
      toastSuccess(toast, {
        summary: t('common.toast.success.update'),
        detail: t('settings.params.account.toast.update.information'),
      });
      getAllParking();
      isFormModalOpened.value = false;
    })
    .catch((error: ApiError) => {
      toastError(toast, {
        summary: t('common.toast.error'),
        detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
      });

      // Throw error to set at false the var __alreadyClicked in v-one-click directive
      throw error;
    });
};

/**
 * Closes the delete parking confirmation modal and deletes the parking entry if confirmed.
 * @param confirm - Whether the deletion was confirmed.
 */
const closeConfirmModal = async (confirm: boolean) => {
  const personApi = new PersonApi(medServerApiConfig);
  const pwr = appUserStore.currentHealthGroupMembership?.pwr;
  if (!pwr) return;

  if (confirm) {
    await personApi.deleteDoctorParking({ doctorId: pwr.personWithRoleId, parkingId: currentParking.value.uuid })
    .then(() => {
      toastSuccess(toast, {
        summary: t('common.toast.success.update'),
        detail: t('settings.params.account.toast.delete.parking'),
      });
      getAllParking();
      isConfirmModalOpened.value = false;
    })
    .catch((error: ApiError) => {
      toastError(toast, {
        summary: t('common.toast.error'),
        detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
      });

      // Throw error to set at false the var __alreadyClicked in v-one-click directive
      throw error;
    });
  } else {
    isConfirmModalOpened.value = false;
  }
};

/**
 * Opens the delete parking confirmation modal for the given parking.
 * @param parking - The parking entry to delete.
 */
const openConfirmModal = (parking: ParkingAvailable) => {
  dropd.value = "";
  currentParking.value = { ...parking };
  isConfirmModalOpened.value = true;
};

/**
 * Fetches all parking entries for the current doctor and updates the reactive list.
 */
const getAllParking = async () => {  
  await personApi.getParkingLots({
    doctorId: appUserStore.currentHealthGroupMembership?.pwr.personWithRoleId
  })
  .then((response: ParkingAvailable[]) => {
    parkings.value = response;
  })
  .catch((error: ApiError) => {
    toastError(toast, {
      summary: t('common.toast.error'),
      detail: error.status ? t(`error.${error.status}`) : t('error.DEFAULT')
    });
  });
};

/**
 * Parking form modal header key, changes depending on add or update mode.
 */
const parkingHeaderKey = computed(() =>
    currentParking.value?.uuid
        ? t('settings.params.account.institutes.portailPatient.parking.update')
        : t('settings.params.account.institutes.portailPatient.parking.add')
)

/**
 * Computes the current institute UUID from the store.
 * Returns an empty string if no institute is selected.
 */
const currInstituteUUID = computed(() => {
  if (!appUserStore.currentHealthGroupMembership) {return "";}
  return appUserStore.currentHealthGroupMembership.instituteWithRole.instituteWithRoleId || "";
});

watch(currInstituteUUID, async (newVal) => {
  if (newVal) {
    await getAllParking();
  }
}, { immediate: true });


return (_ctx: any,_cache: any) => {
  const _component_svg_item = _resolveComponent("svg-item")!
  const _component_text_item = _resolveComponent("text-item")!
  const _component_buttonGroup = _resolveComponent("buttonGroup")!
  const _component_es_button_icon = _resolveComponent("es-button-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_buttonGroup, {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (openAddForm()))
      }, {
        default: _withCtx(() => [
          _createVNode(_component_svg_item, { icon: "plus" }),
          _createVNode(_component_text_item, {
            txt: _unref(t)('action.add'),
            styles: "font-mBold"
          }, null, 8, ["txt"])
        ]),
        _: 1
      })
    ]),
    (parkings.value && parkings.value.length > 0)
      ? (_openBlock(), _createBlock(_unref(DataTable), {
          key: 0,
          value: parkings.value,
          "data-key": "uuid",
          "striped-rows": "",
          class: "mtop-06"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Column), {
              field: "parking",
              header: _unref(t)('settings.label.instituteInformation.name'),
              style: {"min-width":"50rem"},
              class: "table-main-col"
            }, null, 8, ["header"]),
            _createVNode(_unref(Column), {
              field: "actions",
              header: _unref(t)('settings.label.instituteInformation.action')
            }, {
              body: _withCtx(({ data }) => [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_es_button_icon, {
                    icon: "pencil_fill",
                    folder: "shareIcons",
                    class: "pad-l",
                    styles: "img-primary",
                    onClick: ($event: any) => (openUpdateForm(data))
                  }, null, 8, ["onClick"]),
                  _createVNode(_component_es_button_icon, {
                    icon: "trash",
                    folder: "shareIcons",
                    class: "pad-l",
                    styles: "img-primary",
                    onClick: ($event: any) => (openConfirmModal(data))
                  }, null, 8, ["onClick"])
                ])
              ]),
              _: 1
            }, 8, ["header"])
          ]),
          _: 1
        }, 8, ["value"]))
      : _createCommentVNode("", true),
    _createVNode(EsParkingForm, {
      isOpen: isFormModalOpened.value,
      parking: currentParking.value,
      header: _unref(t)(parkingHeaderKey.value),
      "confirm-method": onParkingModalConfirm,
      onModalClose: _cache[1] || (_cache[1] = ($event: any) => (onParkingModalClose()))
    }, null, 8, ["isOpen", "parking", "header"]),
    _createVNode(EsConfirmDialog, {
      isOpen: isConfirmModalOpened.value,
      onModalClose: _cache[2] || (_cache[2] = () => closeConfirmModal(false)),
      header: _unref(t)('settings.params.account.institutes.portailPatient.parking.delete'),
      message: _unref(t)('settings.params.account.institutes.portailPatient.parking.message'),
      "on-confirm": () => closeConfirmModal(true)
    }, null, 8, ["isOpen", "header", "message", "on-confirm"])
  ], 64))
}
}

})